import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/client';

const EMPTY_FORM = { title: '', type: 'rfp', opensAt: '', closesAt: '' };

export default function EventsPage() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/sourcing/events');
      setEvents(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patch(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Only send the optional datetimes when set — the validator is
      // z.string().datetime().optional(), which rejects an empty string; and a
      // datetime-local value must be normalised to full ISO (with Z).
      const body = { title: form.title, type: form.type };
      if (form.opensAt) body.opensAt = new Date(form.opensAt).toISOString();
      if (form.closesAt) body.closesAt = new Date(form.closesAt).toISOString();
      await api.post('/sourcing/events', body);
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="sourcing-events-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Sourcing Events</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="sourcing-event-add" onClick={() => setAddOpen(true)}>
          Create Event
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Opens</th>
              <th>Closes</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr
                key={ev.id}
                data-testid={`sourcing-event-row-${ev.id}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/sourcing/events/${ev.id}`)}
              >
                <td>{ev.title}</td>
                <td>{ev.type}</td>
                <td>{ev.status}</td>
                <td>{ev.opens_at?.slice(0, 10)}</td>
                <td>{ev.closes_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {!events.length && <tr><td colSpan={5}>No sourcing events yet.</td></tr>}
          </tbody>
        </table>
      )}

      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 380 }}>
            <h3>Create Sourcing Event</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  data-testid="sourcing-event-title-input"
                  placeholder="Event title"
                  value={form.title}
                  onChange={(e) => patch('title', e.target.value)}
                  required
                />
                <select data-testid="sourcing-event-type-select" value={form.type} onChange={(e) => patch('type', e.target.value)}>
                  <option value="rfp">RFP</option>
                  <option value="rfi">RFI</option>
                  <option value="auction">Reverse Auction</option>
                </select>
                <label>
                  Opens At
                  <input
                    type="datetime-local"
                    value={form.opensAt}
                    onChange={(e) => patch('opensAt', e.target.value)}
                  />
                </label>
                <label>
                  Closes At
                  <input
                    type="datetime-local"
                    value={form.closesAt}
                    onChange={(e) => patch('closesAt', e.target.value)}
                  />
                </label>
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="sourcing-event-form-submit" disabled={saving}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setError(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
